import * as React from "react";

/**
 * Breakpoints matching Tailwind CSS defaults
 */
export const BREAKPOINTS = {
  sm: 640,   // Mobile
  md: 768,   // Tablet
  lg: 1024,  // Desktop
  xl: 1280,  // Large desktop
};

/**
 * Hook to detect mobile viewport
 *
 * Uses matchMedia to detect viewport width changes
 * Mobile breakpoint: 768px (matching Tailwind md breakpoint)
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(undefined);

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${BREAKPOINTS.md - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < BREAKPOINTS.md);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < BREAKPOINTS.md);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return !!isMobile;
}

/**
 * Hook to detect viewport size with multiple breakpoints
 *
 * Returns an object with boolean flags for each breakpoint:
 * - isMobile: < 640px (sm)
 * - isTablet: 640px - 1023px
 * - isDesktop: >= 1024px (lg)
 *
 * Also returns current breakpoint name: 'mobile' | 'tablet' | 'desktop'
 */
export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = React.useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    current: 'desktop',
  });

  React.useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;

      if (width < BREAKPOINTS.sm) {
        setBreakpoint({
          isMobile: true,
          isTablet: false,
          isDesktop: false,
          current: 'mobile',
        });
      } else if (width < BREAKPOINTS.lg) {
        setBreakpoint({
          isMobile: false,
          isTablet: true,
          isDesktop: false,
          current: 'tablet',
        });
      } else {
        setBreakpoint({
          isMobile: false,
          isTablet: false,
          isDesktop: true,
          current: 'desktop',
        });
      }
    };

    // Set initial value
    updateBreakpoint();

    // Listen for changes
    window.addEventListener('resize', updateBreakpoint);
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, []);

  return breakpoint;
}

/**
 * Hook to check if viewport matches a media query
 *
 * @param {string} query - Media query string (e.g., '(max-width: 640px)')
 * @returns {boolean} - Whether the media query matches
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);

    mql.addEventListener("change", onChange);
    setMatches(mql.matches);

    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
